// h5pack 壳原生能力桥封装（H5PackBridge，vendor 于 public/h5pack-bridge/）
// - 壳内（visitor-pad APK / 任意 h5pack 应用）：window.ReactNativeWebView 存在，
//   h5packBridge.isAvailable=true，可调用 camera（扫码/拍照）/location 等原生能力；
// - 纯浏览器：isAvailable=false，所有调用方须走浏览器降级路径（getUserMedia / 无硬件能力）。
//
// 注意：h5pack 壳的「扫码」能力要求壳工程 h5pack.json 的 scanEnabled: true，
// 否则原生侧返回 SCAN_NOT_ENABLED（见 h5pack-native CameraModule.scan）。

/**
 * 环境判定（全站统一的唯一出口，页面据此决定走原生还是浏览器降级）：
 * - 'h5pack'：运行在 h5pack 壳（安卓 APK）内，扫码/拍照等走原生桥；
 * - 'browser'：纯浏览器，走 getUserMedia 等浏览器降级路径。
 *
 * 判定依据：h5packBridge 由 vendor 脚本在 head 里同步加载，其构造时以
 * window.ReactNativeWebView 是否存在置 isAvailable（RN WebView 在页面脚本
 * 执行前注入该对象，因此时序可靠）；调用时再双查一次防加载时序边界。
 */
export function getEnvType() {
    if (isBridgeAvailable()) return 'h5pack'
    return 'browser'
}

export function isBridgeAvailable() {
    const bridge = window.h5packBridge
    if (!bridge) return false
    // 双重校验：桥自身的判定 + RN WebView 注入对象（任一存在即认为在壳内可调用）
    return !!(bridge.isAvailable || window.ReactNativeWebView)
}

export function isInH5PackShell() {
    return getEnvType() === 'h5pack'
}

// 环境概要（设置页展示 / 连接上报用）
export function getEnvInfo() {
    return {
        envType: getEnvType(),
        os: parseOSInfo(),
        ...getDeviceInfo(),
        fingerprint: getDeviceFingerprint(),
    }
}

// 系统信息解析（设置页展示用）：安卓/iOS 带版本号，如「安卓 Android 13」「iPadOS 17.4」
export function parseOSInfo(ua) {
    const userAgent = ua || navigator.userAgent
    const android = userAgent.match(/Android\s+([\d.]+)/)
    if (android) return '安卓 Android ' + android[1]
    if (/iPad|iPhone|iPod/.test(userAgent)) {
        const m = userAgent.match(/OS\s+([\d_]+)/)
        const ver = m ? ' ' + m[1].replace(/_/g, '.') : ''
        return (/iPad/.test(userAgent) ? 'iPadOS' : 'iOS') + ver
    }
    if (/Windows/.test(userAgent)) {
        const nt = userAgent.match(/Windows NT ([\d.]+)/)
        const map = {'10.0': '10/11', '6.3': '8.1', '6.2': '8', '6.1': '7'}
        return 'Windows' + (nt && map[nt[1]] ? ' ' + map[nt[1]] : '')
    }
    if (/Mac OS X/.test(userAgent)) {
        const m = userAgent.match(/Mac OS X\s+([\d_]+)/)
        return 'macOS' + (m ? ' ' + m[1].replace(/_/g, '.') : '')
    }
    return '未知系统'
}

// 机型解析：Android WebView/Chrome 的 UA 带真实机型（如 SM-X200）；iPad/iPhone 只到品类
export function parseDeviceModel(ua) {
    const userAgent = ua || navigator.userAgent
    const android = userAgent.match(/Android[^;)]*;\s*([^;)]+?)(?:\s+Build\/\S+)?\s*[;)]/)
    if (android && android[1]) {
        const name = android[1].trim()
        if (name.toLowerCase() !== 'wv') return name // wv = WebView 无机型信息
    }
    if (/iPad/i.test(userAgent)) return 'iPad'
    if (/iPhone/i.test(userAgent)) return 'iPhone'
    if (/Windows/i.test(userAgent)) return 'Windows'
    if (/Macintosh/i.test(userAgent)) return 'Mac'
    return 'Unknown'
}

// 设备信息（连接后端 /mobile/device/connect 时上报）
export function getDeviceInfo() {
    const dpr = window.devicePixelRatio || 1
    return {
        source: isBridgeAvailable() ? 'h5pack' : 'browser',
        model: parseDeviceModel(),
        screen: {
            width: Math.round(screen.width * dpr), // 物理分辨率
            height: Math.round(screen.height * dpr),
            cssWidth: screen.width,
            cssHeight: screen.height,
            dpr,
        },
        ua: navigator.userAgent,
    }
}

// 设备指纹（同步读法，展示用）：优先返回已解析的稳定指纹缓存，否则本地随机串。
// 连接/重连上报请用 resolveDeviceFingerprint()（异步，含壳内 ANDROID_ID 解析）。
export function getDeviceFingerprint() {
    if (resolvedFingerprint) return resolvedFingerprint
    const KEY = 'pad.device.fingerprint'
    let fp = localStorage.getItem(KEY)
    if (!fp) {
        fp = 'H5' + Date.now().toString(36).toUpperCase() + Math.random().toString(36).slice(2, 8).toUpperCase()
        localStorage.setItem(KEY, fp)
    }
    return fp
}

// 设备指纹（异步解析，连接/重连上报用）：
// - 壳内：优先壳原生桥 device.info 返回的系统级 ANDROID_ID（清数据/卸载重装不变，
//   免授权码重连的持久凭据）；拿不到再降级本地随机串。
// - 浏览器：本地持久化随机串（清浏览器数据后变化，重连退回向导属预期）。
// 结果进程内缓存，多次调用只解析一次。
export function resolveDeviceFingerprint() {
    if (!fingerprintPromise) {
        fingerprintPromise = (async () => {
            let fp = ''
            if (isBridgeAvailable()) {
                try {
                    const info = await shellBridgeCall('device', 'info', {})
                    const id = info && info.androidId ? String(info.androidId).trim() : ''
                    // 部分国产 ROM/工控平板的 ANDROID_ID 恒为全 0（fk 真机实锤），不具设备唯一性：
                    // 多台设备会同指纹，免授权重连按指纹找授权码会串设备——弃用，降级本地指纹
                    if (id && !/^0+$/.test(id)) {
                        fp = 'AND' + id.toUpperCase()
                    }
                } catch (e) {
                    console.log('device.info fallback to local fingerprint:', e && e.message)
                }
            }
            if (!fp) fp = getDeviceFingerprint()
            resolvedFingerprint = fp
            return fp
        })()
    }
    return fingerprintPromise
}

let fingerprintPromise = null
let resolvedFingerprint = ''

// 直连壳桥的底层调用（vendor 桥没有 device 模块，按同一协议自行收发）：
// 发 {type:'bridge_call', module, action, params, callId} → 收 {type:'bridge_response', callId, success, data}
// timeoutMs 可放宽单次调用超时（vendor 桥统一 30s，拍照等人工操作不够用）
function shellBridgeCall(module, action, params, timeoutMs) {
    return new Promise((resolve, reject) => {
        if (!isBridgeAvailable()) {
            reject(new Error('BRIDGE_UNAVAILABLE'))
            return
        }
        const callId = 'pad_' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6)
        const onMessage = (event) => {
            let data
            try {
                data = JSON.parse(event.data)
            } catch (e) {
                return
            }
            if (!data || data.type !== 'bridge_response' || data.callId !== callId) return
            cleanup()
            if (data.success) resolve(data.data)
            else reject(new Error((data.error && data.error.message) || 'BRIDGE_ERROR'))
        }
        const cleanup = () => {
            document.removeEventListener('message', onMessage)
            window.removeEventListener('message', onMessage)
        }
        document.addEventListener('message', onMessage)
        window.addEventListener('message', onMessage)
        window.ReactNativeWebView.postMessage(JSON.stringify({
            type: 'bridge_call',
            callId,
            module,
            action,
            params: params || {},
            timestamp: Date.now(),
        }))
        setTimeout(() => {
            cleanup()
            reject(new Error('BRIDGE_CALL_TIMEOUT'))
        }, timeoutMs || 10000)
    })
}

// 原生扫码（壳内）：返回二维码/条码内容字符串；用户点「取消」返回 null；壳外抛 BRIDGE_UNAVAILABLE
export async function nativeScanCode() {
    const bridge = window.h5packBridge
    if (!isBridgeAvailable()) {
        throw new Error('BRIDGE_UNAVAILABLE')
    }
    const result = await bridge.camera.scan()
    if (result && typeof result === 'object' && result.canceled) {
        // 壳侧补丁⑥c 起「取消」显式回传 canceled（早期只关浮层不回调，H5 要等桥超时才复位）
        return null
    }
    if (result && typeof result === 'object' && result.base64 !== undefined) {
        // 防御：若原生返回资产对象而非码串
        throw new Error('SCAN_UNEXPECTED_RESULT')
    }
    return result
}

// 原生拍照（壳内，默认前置）：返回 { base64, ... }，用户取消返回 { canceled: true }；壳外抛 BRIDGE_UNAVAILABLE
// 不走 vendor 桥（其统一 30s 超时，人工摆姿势拍照经常超），改走自建通道放宽到 120s
export async function nativeTakePhoto(cameraType) {
    if (!isBridgeAvailable()) {
        throw new Error('BRIDGE_UNAVAILABLE')
    }
    return shellBridgeCall('camera', 'open', {cameraType: cameraType || 'front'}, 120000)
}
